import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { hrAPI } from '../lib/api';
import { UserCircle, Loader2, Save, Shield, Home, Phone, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  HrDocumentsTab, HrLeaveTab, HrTrainingTab,
} from '../components/hr/HrRecordsTabs';

const Field = ({ label, value, onChange, type = 'text', placeholder = '', testId }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

/**
 * Self-service HR profile.
 *
 * Every authenticated user can edit their own contact, address, emergency
 * contact and next-of-kin fields. Salary / bank / NI / UTR / tax code are
 * always managed by an HR Admin and are hidden here (the API redacts them).
 */
export const HrMyProfile = () => {
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    hrAPI.myProfile()
      .then(r => setEmp(r.data))
      .catch(() => toast.error('Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => setEmp(prev => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        personal_email: emp.personal_email,
        personal_phone: emp.personal_phone,
        address_line1: emp.address_line1,
        address_line2: emp.address_line2,
        address_city: emp.address_city,
        address_postcode: emp.address_postcode,
        address_country: emp.address_country,
        emergency_contact_name: emp.emergency_contact_name,
        emergency_contact_phone: emp.emergency_contact_phone,
        emergency_contact_relationship: emp.emergency_contact_relationship,
        next_of_kin_name: emp.next_of_kin_name,
        next_of_kin_phone: emp.next_of_kin_phone,
        next_of_kin_relationship: emp.next_of_kin_relationship,
      };
      const r = await hrAPI.updateMyProfile(payload);
      setEmp(r.data);
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading || !emp) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="hr-my-profile-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-3">
              <UserCircle className="w-7 h-7 text-blue-600" /> My Profile
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {emp.first_name} {emp.last_name} · {emp.job_title || emp.work_email}
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xs">
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>You can edit your contact details, address, emergency contact and next-of-kin. Salary, bank, NI, UTR and tax code are managed by HR.</span>
        </div>

        <Tabs defaultValue="profile">
          <TabsList>
            <TabsTrigger value="profile" data-testid="tab-my-profile">Profile</TabsTrigger>
            <TabsTrigger value="leave" data-testid="tab-my-leave">My Leave</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-my-documents">My Documents</TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-my-training">My Training</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6 mt-6">
            <Card className="border-slate-200" data-testid="section-contact">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4 text-slate-500" /> Contact Details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Personal Email" type="email" value={emp.personal_email} onChange={(v) => update({ personal_email: v })} placeholder="alex@gmail.com" testId="my-pemail" />
              <Field label="Personal Phone" value={emp.personal_phone} onChange={(v) => update({ personal_phone: v })} placeholder="+44 7700 900000" testId="my-pphone" />
            </div>
            <div className="text-[11px] text-slate-500">
              Work email <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px] ml-1">{emp.work_email || '—'}</Badge> is managed by HR.
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="section-home-address">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Home className="w-4 h-4 text-slate-500" /> Home Address</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field label="Address Line 1" value={emp.address_line1} onChange={(v) => update({ address_line1: v })} testId="my-addr1" />
            <Field label="Address Line 2" value={emp.address_line2} onChange={(v) => update({ address_line2: v })} testId="my-addr2" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="City" value={emp.address_city} onChange={(v) => update({ address_city: v })} testId="my-city" />
              <Field label="Postcode" value={emp.address_postcode} onChange={(v) => update({ address_postcode: v })} testId="my-postcode" />
              <Field label="Country" value={emp.address_country} onChange={(v) => update({ address_country: v })} placeholder="United Kingdom" testId="my-country" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="section-emergency">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Heart className="w-4 h-4 text-rose-500" /> Emergency Contact</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Name" value={emp.emergency_contact_name} onChange={(v) => update({ emergency_contact_name: v })} testId="my-em-name" />
              <Field label="Phone" value={emp.emergency_contact_phone} onChange={(v) => update({ emergency_contact_phone: v })} testId="my-em-phone" />
              <Field label="Relationship" value={emp.emergency_contact_relationship} onChange={(v) => update({ emergency_contact_relationship: v })} placeholder="e.g. Spouse, Parent" testId="my-em-rel" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="section-nok">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Heart className="w-4 h-4 text-slate-500" /> Next of Kin</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Name" value={emp.next_of_kin_name} onChange={(v) => update({ next_of_kin_name: v })} testId="my-nok-name" />
              <Field label="Phone" value={emp.next_of_kin_phone} onChange={(v) => update({ next_of_kin_phone: v })} testId="my-nok-phone" />
              <Field label="Relationship" value={emp.next_of_kin_relationship} onChange={(v) => update({ next_of_kin_relationship: v })} testId="my-nok-rel" />
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="leave" className="mt-6">
            <HrLeaveTab employeeId={emp.id} canApprove={false} isSelf={true} isHrAdmin={false} />
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <HrDocumentsTab employeeId={emp.id} canWrite={true} isSelf={true} isHrAdmin={false} />
          </TabsContent>

          <TabsContent value="training" className="mt-6">
            <HrTrainingTab employeeId={emp.id} isHrAdmin={false} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default HrMyProfile;
